import { useMemo } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { MetricsWindow, PerfTargets } from '@/services/perfTestsApi';

interface Props {
  windows: MetricsWindow[];
  targets?: PerfTargets;
}

const TAG_COLORS: Record<string, string> = {
  crud: '#6366f1',
  aggregation: '#10b981',
  llm: '#f59e0b',
  sse: '#a855f7',
  auth: '#ef4444',
};

// Le tag « sse » mesure http_req_waiting (TTFB = headers + 1er event reçu),
// pas http_req_duration. C'est ce que perçoit l'EventSource browser quand
// il passe en état 'open'. Voir thresholds.js pour la justification.
const TAG_LABELS: Record<string, string> = {
  crud: 'crud',
  aggregation: 'aggregation',
  llm: 'llm',
  sse: 'sse (TTFB)',
  auth: 'auth',
};

function fmtTime(ms: number): string {
  const d = new Date(ms);
  return `${d.getMinutes().toString().padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')}`;
}

export function LiveMetricsChart({ windows, targets }: Props) {
  const data = useMemo(
    () =>
      windows.map((w) => ({
        time: fmtTime(w.windowEndMs),
        rps: Math.round(w.rps * 10) / 10,
        errorRate: Math.round(w.errorRate * 10000) / 100, // en %
        vus: w.vus,
        crud: w.p95.crud,
        aggregation: w.p95.aggregation,
        llm: w.p95.llm,
        sse: w.p95.sse,
        auth: w.p95.auth,
      })),
    [windows],
  );

  if (data.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic text-center py-8">
        En attente des premières métriques…
      </div>
    );
  }

  const last = data[data.length - 1];

  // Compute breach state per metric for KPI tone
  const errorBreached = targets ? last.errorRate / 100 > targets.errorRate : false;
  const crudBreached = targets && last.crud != null ? last.crud > targets.p95CrudMs : false;
  const aggBreached = targets && last.aggregation != null ? last.aggregation > targets.p95AggregationMs : false;
  const llmBreached = targets && last.llm != null ? last.llm > targets.p95LlmMs : false;
  const sseBreached = targets && last.sse != null ? last.sse > targets.p95SseMs : false;
  const authBreached = targets && last.auth != null ? last.auth > targets.p95AuthMs : false;
  const anyLatencyBreached = crudBreached || aggBreached || llmBreached || sseBreached || authBreached;

  // Targets ordered for ReferenceLines, with shared target Y axis (max of all targets bounds the view)
  const TARGET_LINES = targets
    ? [
        { tag: 'crud', y: targets.p95CrudMs },
        { tag: 'aggregation', y: targets.p95AggregationMs },
        { tag: 'llm', y: targets.p95LlmMs },
        { tag: 'sse', y: targets.p95SseMs },
        { tag: 'auth', y: targets.p95AuthMs },
      ]
    : [];

  return (
    <div className="space-y-4">
      {/* KPIs en chiffre */}
      <div className="grid grid-cols-3 gap-3">
        <Kpi label="RPS" value={last.rps.toFixed(1)} unit="req/s" />
        <Kpi label="VUs actifs" value={String(last.vus)} unit="" />
        <Kpi
          label="Taux d'erreur"
          value={last.errorRate.toFixed(2)}
          unit="%"
          target={targets ? `< ${(targets.errorRate * 100).toFixed(2)} %` : undefined}
          breached={errorBreached}
        />
      </div>

      {/* Statut global latence */}
      {targets && (
        <div
          className={`text-xs font-medium px-3 py-2 rounded-md border ${
            anyLatencyBreached
              ? 'bg-red-50 border-red-200 text-red-800'
              : 'bg-emerald-50 border-emerald-200 text-emerald-800'
          }`}
        >
          {anyLatencyBreached
            ? '⚠ Au moins une latence p95 dépasse sa cible go-live'
            : '✓ Toutes les latences p95 sont sous leur cible go-live'}
        </div>
      )}

      {/* Graphe latence p95 par tag avec lignes cibles */}
      <div className="bg-white border border-gray-200 rounded-md p-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-medium text-gray-700">Latence p95 par tag (ms)</h3>
          {targets && (
            <span className="text-[10px] text-gray-500">Lignes pointillées = cibles go-live</span>
          )}
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="time" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {(['crud', 'aggregation', 'llm', 'sse', 'auth'] as const).map((tag) => (
              <Line
                key={tag}
                type="monotone"
                dataKey={tag}
                name={TAG_LABELS[tag]}
                stroke={TAG_COLORS[tag]}
                strokeWidth={2}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            ))}
            {TARGET_LINES.map((t) => (
              // ifOverflow="discard" : la ligne est masquée tant que la
              // cible est hors plage de l'axe Y. Sinon les cibles
              // « lentes » (aggregation 3 s) écrasent les latences réelles
              // (10–50 ms) en bas. La ligne apparaît dès qu'une métrique
              // approche sa cible. Les chiffres « valeur / cible » sous
              // le graphe gardent l'info exacte, quel que soit le scale.
              <ReferenceLine
                key={`ref-${t.tag}`}
                y={t.y}
                stroke={TAG_COLORS[t.tag]}
                strokeDasharray="4 4"
                strokeOpacity={0.6}
                ifOverflow="discard"
                label={{
                  value: `${TAG_LABELS[t.tag] ?? t.tag} ${t.y}ms`,
                  position: 'right',
                  fill: TAG_COLORS[t.tag],
                  fontSize: 10,
                  opacity: 0.8,
                }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>

        {/* Légende KPIs latence par tag */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mt-3 text-xs">
          {(
            [
              { tag: 'crud', value: last.crud, target: targets?.p95CrudMs, breached: crudBreached },
              { tag: 'aggregation', value: last.aggregation, target: targets?.p95AggregationMs, breached: aggBreached },
              { tag: 'llm', value: last.llm, target: targets?.p95LlmMs, breached: llmBreached },
              { tag: 'sse', value: last.sse, target: targets?.p95SseMs, breached: sseBreached },
              { tag: 'auth', value: last.auth, target: targets?.p95AuthMs, breached: authBreached },
            ] as const
          ).map((m) => (
            <div
              key={m.tag}
              className={`px-2 py-1 rounded border ${
                m.breached ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="text-gray-500">{TAG_LABELS[m.tag] ?? m.tag}</div>
              <div
                className={`tabular-nums font-medium ${m.breached ? 'text-red-700' : 'text-gray-900'}`}
              >
                {m.value != null ? `${Math.round(m.value)} ms` : '—'}
                {m.target != null && (
                  <span className="text-[10px] text-gray-500 ml-1">/ {m.target}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Graphe RPS + erreurs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="bg-white border border-gray-200 rounded-md p-3">
          <h3 className="text-xs font-medium text-gray-700 mb-2">RPS</h3>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="time" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line type="monotone" dataKey="rps" stroke="#6366f1" strokeWidth={2} dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-gray-200 rounded-md p-3">
          <h3 className="text-xs font-medium text-gray-700 mb-2">
            Taux d'erreur (%)
            {targets && (
              <span className="text-[10px] text-gray-500 ml-2">
                cible &lt; {(targets.errorRate * 100).toFixed(2)} %
              </span>
            )}
          </h3>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="time" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line type="monotone" dataKey="errorRate" stroke="#ef4444" strokeWidth={2} dot={false} isAnimationActive={false} />
              {targets && (
                <ReferenceLine
                  y={targets.errorRate * 100}
                  stroke="#ef4444"
                  strokeDasharray="4 4"
                  strokeOpacity={0.6}
                  label={{ value: `cible ${(targets.errorRate * 100).toFixed(2)}%`, position: 'right', fill: '#ef4444', fontSize: 10 }}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function Kpi({
  label,
  value,
  unit,
  target,
  breached,
}: {
  label: string;
  value: string;
  unit: string;
  target?: string;
  breached?: boolean;
}) {
  const valueColor = breached ? 'text-red-600' : 'text-gray-900';
  const bg = breached ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200';
  return (
    <div className={`border rounded-md p-3 ${bg}`}>
      <div className="text-xs text-gray-500 flex items-center justify-between">
        <span>{label}</span>
        {target && <span className="text-[10px] text-gray-400">cible {target}</span>}
      </div>
      <div className={`text-2xl font-semibold tabular-nums ${valueColor}`}>
        {value} <span className="text-sm font-normal text-gray-500">{unit}</span>
      </div>
    </div>
  );
}
